import React from 'react'
import { OrdersContext } from 'contexts/OrdersContext'
import _ from 'lodash'
import {
  DcButton,
  PixelAlert,
  PixelDropDownMenu,
  PixelFactoryContext,
  PixelFlexBox,
  PixelModal,
  PixelText,
  SectionContent,
  DcOverlaySlider,
} from 'pixel-base'
import { AjaxResponse } from 'rxjs/ajax'

import {
  cancelConsignment,
  createConsignment,
  downLoadFile,
  markReadyToShip,
  post,
  updateTags,
} from 'utils/api-routes.util'

import OrdersCreateBulkConsignments from './OrdersCreateBulkConsignments'
import { groupStatus } from './OrderStatus'
import OrdersUpdateShippingInfo from './OrdersUpdateShippingInfo'
import PrepareOrdersDispatch from './PrepareDispatch'
import { OrderSummary, IStoreList, IStatusGroup } from '../../../../decl'
import { IPixelFactory } from '../../../../decl.d'

interface IProps {
  refresh: () => void
  handleClearRows: () => void
  selectedOrders: OrderSummary[]
  selectedStore: IStoreList
  handleUpdate: (data: any, action: string) => void
}
const OrdersBulkEdit = (props: IProps) => {
  const {
    refresh,
    handleClearRows,
    selectedOrders,
    selectedStore,
    handleUpdate,
  } = props

  const { state } = React.useContext(OrdersContext)
  const {
    showFailure,
    showSuccess,
    pixelConfirm,
    hidePageLoader,
    pixelPrompt,
    showPageLoader,
  } = React.useContext<IPixelFactory>(PixelFactoryContext)
  const [showUpdateShippingInfo, setShowUpdateShippingInfo] =
    React.useState(false)
  const customOptions = {
    responseType: 'arraybuffer',
    observe: 'response',
  }
  const [cities, setCities] = React.useState<Array<{ name: string }>>([])
  const [showBulkConsignment, setShowBulkConsignment] = React.useState(false)
  const [PrepareDispatch, setPrepareDispatch] = React.useState(false)
  const [groupedStatus, setGroupedStatus] = React.useState<IStatusGroup[]>([])

  const [cancelConsignmentError, setCancelConsignmentError] =
    React.useState(false)
  const [cancelConsignmentMessage, setCancelConsignmentMessage] =
    React.useState('')
  const [createConsignmentError, setCreateConsignmentError] =
    React.useState(false)
  const [createConsignmentMessage, setCreateConsignmentMessage] =
    React.useState('')
  const exportSelectedOrders = () => {
    showPageLoader()
    post(
      '/orders/bulk/export',
      {
        order_ids: selectedOrders.map((order) => order.number),
      },
      customOptions,
    ).subscribe((res: AjaxResponse<any>) => {
      downLoadFile(
        res.response as ArrayBuffer,
        'Orders.xlsx',
        'application/vnd.ms-excel',
      )
      hidePageLoader()
    })
  }

  const selectedOrderLineItems = (option: 'print' | 'export') => {
    showPageLoader()
    post(
      '/orders/bulk/export-line-items',
      {
        order_ids: selectedOrders.map((order) => order.number),
        format: option === 'print' ? 'pdf' : 'xlsx',
      },
      customOptions,
    ).subscribe((res: AjaxResponse<any>) => {
      if (option === 'print') {
        downLoadFile(
          res.response as ArrayBuffer,
          'Orders Products.pdf',
          'application/pdf',
        )
      } else {
        downLoadFile(
          res.response as ArrayBuffer,
          'Orders Products.xlsx',
          'application/vnd.ms-excel',
        )
      }
      hidePageLoader()
    })
  }

  const exportOptions = [
    {
      label: 'Download Orders (Excel)',
      disabled: false,
      clickHandler: exportSelectedOrders,
    },
    {
      label: 'Download Order Products (Excel)',
      disabled: false,
      clickHandler: () => selectedOrderLineItems('export'),
    },
    {
      label: 'Download Order Products (PDF)',
      disabled: false,
      clickHandler: () => selectedOrderLineItems('print'),
    },
    {
      label: 'Download Invoices (PDF)',
      disabled: false,
      clickHandler: () => {
        bulkInvoice()
      },
    },
  ]
  const darazOptions = [
    {
      label: 'Mark Ready To Ship',
      disabled: false,
      clickHandler: () => {
        pixelConfirm({
          title: 'Confirmation',
          message: `Do you want to mark selected ${selectedOrders.length} orders 'Ready to Ship' on your Daraz store.`,
          onConfirm: () => {
            showPageLoader()
            const data = {
              store_id: selectedStore?.store_id,
              order_ids: selectedOrders.map((order) => order.number),
            }
            markReadyToShip(data).subscribe(() => {
              hidePageLoader()
              handleUpdate()
            })
          },
        })
      },
    },
    {
      label: 'Print Invoices',
      disabled: false,
      clickHandler: () => {
        bulkInvoiceDaraz()
      },
    },
  ]
  const moreOptions = [
    {
      label: 'Edit Shipping Info',
      disabled: false,
      clickHandler: () => setShowUpdateShippingInfo(true),
    },
    {
      label: 'Create Consignments',
      disabled: false,
      clickHandler: () => {
        let hasTrackingIDs = false
        selectedOrders.forEach((order) => {
          if (
            order['courier']['tracking_id'] !== '' &&
            order['courier']['tracking_id'] !== 'error' &&
            order['courier']['tracking_id'] !== 'timeout'
          ) {
            hasTrackingIDs = true
          }
        })
        if (hasTrackingIDs) {
          setCreateConsignmentMessage('Consignments already exists!')
          setCreateConsignmentError(true)
        } else {
          setShowBulkConsignment(true)
        }
      },
    },
    {
      label: 'Cancel Consignment',
      disabled: false,
      clickHandler: () => {
        const isSameCourier = _.groupBy(
          selectedOrders,
          (order) => order['courier']['courier_name'],
        )
        let hasTrackingIDs = false
        selectedOrders.forEach((order) => {
          if (
            order['courier']['tracking_id'] !== '' &&
            order['courier']['tracking_id'] !== 'error' &&
            order['courier']['tracking_id'] !== 'timeout'
          ) {
            hasTrackingIDs = true
          }
        })
        if (!hasTrackingIDs) {
          setCancelConsignmentMessage(
            'There are no consignments created for the selected orders',
          )
          setCancelConsignmentError(true)
        } else if (Object.keys(isSameCourier).length > 1) {
          setCancelConsignmentMessage(
            'Consignments should belong to same courier to cancel bulk consignments',
          )
          setCancelConsignmentError(true)
        } else {
          pixelPrompt({
            title: 'Confirmation',
            message: `Do you want to cancel the consignment for selected ${selectedOrders.length} orders?<br /><br /> write <strong>force</strong> if you want to cancel Forcefully.`,
            onConfirm: (isForce) => {
              showPageLoader()
              handleClearRows()
              const data: {
                orders: Array<{ order_id: number; tracking_id: string }>
                force?: boolean
              } = { orders: [] }
              selectedOrders.map((o) => {
                data.orders.push({
                  order_id: o['number'],
                  tracking_id: o['courier']['tracking_id'],
                })
              })

              data.force = isForce === 'force'
              cancelConsignment(data).subscribe((res: object) => {
                hidePageLoader()
                const consignments: object = res.response
                let allFailures = true
                for (const key in consignments) {
                  if (Object.hasOwn(consignments, key)) {
                    if (consignments[key].success) {
                      allFailures = false
                      break
                    } else {
                      showFailure(consignments[key].error)
                    }
                  }
                }
                if (!allFailures) {
                  showSuccess('Applied successfully.')
                  handleUpdate(data, 'cancel-consignment')
                }
              })
            },
          })
        }
      },
    },
    {
      label: 'Prepare Dispatch',
      disabled: false,
      clickHandler: () => {
        setPrepareDispatch(true)
      },
    },
  ]
  const bulkInvoice = () => {
    const data = {
      order_ids: selectedOrders.map((order) => order.number),
    }
    showPageLoader()
    post('/orders/bulk/invoices', data, customOptions).subscribe(
      (res: AjaxResponse<any>) => {
        if (res.response) {
          downLoadFile(
            res.response as ArrayBuffer,
            'Invoices.pdf',
            'application/pdf',
          )
          hidePageLoader()
        } else {
          showFailure('No Invoices Found')
        }
      },
    )
  }
  const bulkInvoiceDaraz = () => {
    const data = {
      order_ids: selectedOrders.map((order) => order.number),
      store_id: selectedOrders[0].store_id,
    }
    showPageLoader()
    customOptions.responseType = 'html'
    post('/orders/bulk/invoices/daraz', data, customOptions).subscribe(
      (res: AjaxResponse<any>) => {
        hidePageLoader()
        if (res.response) {
          const html = res.response
          const newWindow = window.open()
          newWindow.document.write(html)
          newWindow.document.close()
        }
      },
    )
  }

  React.useEffect(() => {
    setCities(
      state?.citiesList?.map((city) => {
        return { name: city.toLowerCase() }
      }) || [],
    )
  }, [state])

  React.useEffect(() => {
    const statuses = groupStatus(state)
    setGroupedStatus(statuses)
  }, [state?.allowedStatuses])

  const options = () => {
    const isOrderClassificationId2 = selectedOrders.some(
      (order) => order.status_classification_id === 2,
    )

    let filteredGroupedStatus = groupedStatus
    if (isOrderClassificationId2) {
      filteredGroupedStatus = groupedStatus.filter((status) => {
        return status.name === 'Processing' || status.name === 'Cancelled'
      })

      const hasProcessing = filteredGroupedStatus.some(
        (status) => status.name === 'Processing',
      )
      const hasCancelled = filteredGroupedStatus.some(
        (status) => status.name === 'Cancelled',
      )

      if (!hasProcessing) {
        const processingStatus = groupedStatus.find(
          (status) => status.name === 'Processing',
        )
        if (processingStatus) {
          filteredGroupedStatus.push(processingStatus)
        }
      }

      if (!hasCancelled) {
        const cancelledStatus = groupedStatus.find(
          (status) => status.name === 'Cancelled',
        )
        if (cancelledStatus) {
          filteredGroupedStatus.push(cancelledStatus)
        }
      }
    }

    return filteredGroupedStatus.map((status) => {
      return {
        ...status,
        label: status.name,
        formatter: (option, i) => {
          return (
            <PixelFlexBox
              justifyContent="space-between"
              alignItems="center"
              alignContent="center"
              height="auto"
              key={i}
            >
              {option.label}
            </PixelFlexBox>
          )
        },
        children: status.children.map((child) => {
          return {
            ...child,
            label: child.title,
            disabled: false,
            formatter: (option) => {
              return (
                <>
                  {option ? (
                    <PixelFlexBox
                      justifyContent="space-between"
                      alignItems="center"
                      alignContent="center"
                      height="auto"
                    >
                      <PixelText style={{ background: 'none' }}>
                        {option.label}
                      </PixelText>
                    </PixelFlexBox>
                  ) : (
                    'Not available'
                  )}
                </>
              )
            },
            clickHandler: () => {
              pixelConfirm({
                title: 'Confirmation',
                message: `Do you want to change status of selected ${selectedOrders.length} orders to ${child.name}?`,
                onConfirm: () => {
                  showPageLoader()
                  const obj = {}
                  const orderIds = selectedOrders.map((order) => order.number)
                  orderIds.forEach((orderId) => {
                    obj[orderId] = child.id
                  })
                  post('/orders/bulk/status', obj).subscribe(() => {
                    hidePageLoader()
                    showSuccess('Applied successfully.')
                    handleClearRows()
                    refresh()
                  })
                },
              })
            },
          }
        }),
      }
    })
  }

  return (
    <>
      {selectedOrders.some((item) => !item.is_editable) ? (
        <DcButton
          style={{
            minWidth: '105px',
          }}
          size="sm"
          variant="primary"
          onClick={() =>
            showFailure(
              selectedOrders.length > 1
                ? 'Some selected orders have non-editable statuses. Select only editable orders to proceed.'
                : 'This order status can not be edited',
            )
          }
        >
          Edit Status
        </DcButton>
      ) : (
        <PixelDropDownMenu
          size="sm"
          isGrouped
          toggleText={'Edit Status' as any}
          options={options()}
        />
      )}

      <PixelDropDownMenu
        isGrouped={false}
        options={state?.orderTags?.map((item) => {
          return {
            label: item.tag_name,
            clickHandler: () => {
              pixelConfirm({
                title: 'Confirmation',
                message: `Do you want to apply tag ${item.tag_name} on selected ${selectedOrders.length} orders`,
                onConfirm: () => {
                  showPageLoader()
                  const data = {
                    tag_name: item.tag_name,
                    order_ids: selectedOrders.map((order) => order.number),
                  }
                  updateTags(data).subscribe(() => {
                    hidePageLoader()
                    showSuccess(
                      `tag ${item.tag_name} has been applied successfully.`,
                    )
                    handleClearRows()
                    refresh()
                    handleUpdate(data, 'update-tags')
                  })
                },
              })
            },
          }
        })}
        size="sm"
        disabled={selectedOrders.length === 0}
        toggleText={'Update Tags' as any}
        // variant="link"
        // variant={selectedOrders.length === 0 ? "secondary" : "primary"}
      />
      <PixelDropDownMenu
        options={exportOptions}
        size="sm"
        disabled={selectedOrders.length === 0}
        toggleText={'Export Excel' as any}
        // variant="link"
        // variant={selectedOrders.length === 0 ? "secondary" : "primary"}
      />

      {selectedStore?.store_type == 'daraz' && (
        <PixelDropDownMenu
          options={darazOptions}
          size="sm"
          disabled={selectedOrders.length === 0}
          toggleText={'Daraz Options' as any}
          // variant="link"
          // variant={selectedOrders.length === 0 ? "secondary" : "primary"}
        />
      )}
      <PixelDropDownMenu
        options={moreOptions}
        size="sm"
        disabled={selectedOrders.length === 0}
        toggleText={'More Options' as any}
        // variant="link"
        // variant={selectedOrders.length === 0 ? "secondary" : "primary"}
      />
      {showUpdateShippingInfo && (
        <DcOverlaySlider
          title={'Update Shipping Info'}
          width="70%"
          onClose={() => setShowUpdateShippingInfo(false)}
          isOpen={showUpdateShippingInfo}
        >
          <SectionContent>
            {showUpdateShippingInfo && (
              <OrdersUpdateShippingInfo
                handleClearRows={handleClearRows}
                refresh={refresh}
                cities={cities}
                selectedOrders={selectedOrders}
                handleClose={() => {
                  setShowUpdateShippingInfo(false)
                }}
              />
            )}
          </SectionContent>
        </DcOverlaySlider>
      )}
      {showBulkConsignment && (
        <DcOverlaySlider
          title={'Create consignment in bulk'}
          width="70%"
          onClose={() => {
            setShowBulkConsignment(false)
            handleClearRows()
          }}
          isOpen={showBulkConsignment}
        >
          {showBulkConsignment && (
            <SectionContent>
              <OrdersCreateBulkConsignments
                onCreateConsignment={(data) => {
                  showPageLoader()
                  createConsignment(data).subscribe(
                    (res: AjaxResponse<any>) => {
                      if (res.response?.data?.consignments?.error) {
                        showFailure(res.response?.data?.consignments?.error)
                        setShowBulkConsignment(false)
                        handleClearRows()
                        hidePageLoader()
                      } else if (res.response?.data) {
                        showSuccess(
                          data.is_dispatch
                            ? `Consignment created successfully with Dispatch ID ${res.response.data?.dispatch_id} `
                            : 'Consignment created successfully',
                        )
                        const dispatch =
                          res.response.data?.dispatch_id ==
                          data.activeDispatch?.dispatch_id
                        handleUpdate(
                          res.response.data?.consignments,
                          dispatch,
                          'consignments',
                          res.response.data?.dispatch_id,
                        )
                        setShowBulkConsignment(false)
                        hidePageLoader()
                      } else {
                        showFailure(res.response?.error)
                        setShowBulkConsignment(false)
                        handleClearRows()
                        hidePageLoader()
                      }
                    },
                  )
                }}
                selectedOrders={selectedOrders}
                isMustDispatch={false}
              />
            </SectionContent>
          )}
        </DcOverlaySlider>
      )}
      {createConsignmentError && (
        <PixelModal
          title={'Create Bulk Consignments'}
          handleClose={() => setCreateConsignmentError(false)}
          show={createConsignmentError}
        >
          {createConsignmentError && (
            <PixelAlert variant="danger">{createConsignmentMessage}</PixelAlert>
          )}
        </PixelModal>
      )}
      {PrepareDispatch && (
        <DcOverlaySlider
          title={'Prepare Dispatch'}
          width="40%"
          onClose={() => {
            setPrepareDispatch(false)
            handleClearRows()
          }}
          isOpen={PrepareDispatch}
        >
          {PrepareDispatch && (
            <SectionContent>
              <PrepareOrdersDispatch
                selectedOrders={selectedOrders}
                handleUpdate={handleUpdate}
                handleClose={() => {
                  setPrepareDispatch(false)
                  handleClearRows()
                }}
              />
            </SectionContent>
          )}
        </DcOverlaySlider>
      )}
      {cancelConsignmentError && (
        <PixelModal
          title={'Cancel Consignments'}
          handleClose={() => setCancelConsignmentError(false)}
          show={cancelConsignmentError}
        >
          {cancelConsignmentError && (
            <PixelAlert variant="danger">{cancelConsignmentMessage}</PixelAlert>
          )}
        </PixelModal>
      )}
    </>
  )
}

export default OrdersBulkEdit
